import { ThemeProvider } from "@thetreep/ui";
import { themeCnam } from "@thetreep/ui";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { Registration } from "@pages/authentication/Registration";
import { Header } from "@components/layout/Header";
import { Footer } from "@components/layout/Footer";
import { GlobalFreshChat } from "@components/common/FreshChatWrapper";
import { Login } from "@pages/authentication/Login";
import { Error } from "@pages/authentication/Error.tsx";
import { Verification } from "@pages/authentication/Verification";
import { routes } from "@routes";
import { ProtectedRoute } from "@ProtectedRoute.tsx";
import { Recovery } from "@pages/authentication/Recovery.tsx";
import { Settings } from "@pages/authentication/Settings.tsx";
import { BookingHome } from "@pages/booking/BookingHome";
import { Results } from "@pages/booking/results/Results";
import { AdminHome } from "@pages/admin/AdminHome.tsx";
import { DAPManagement } from "@pages/admin/daps/DAPManagement.tsx";
import { DAPDetails } from "@pages/admin/daps/DAPDetails";
import { UserManagement } from "@pages/admin/users/UserManagement";
import { AgencyManagement } from "@pages/admin/agencies/AgencyManagement.tsx";
import { ToasterProvider } from "@context/ToasterProvider";
import { CaisseManagement } from "@pages/admin/caisses/CaisseManagement";
import { CaisseDetails } from "@pages/admin/caisses/CaisseDetails";
import { Search } from "@pages/booking/search/Search";
import { Recap } from "@pages/booking/recap/Recap";
import { Cart } from "@pages/booking/cart/Cart";
import { Payment } from "@pages/booking/payment/Payment";
import { Confirmation } from "@pages/booking/confirmation/Confirmation";
import { Home } from "@pages/home/Home";
import { Help } from "@pages/home/help/Help";
import { useEffect } from "react";
import { useUserStore } from "@stores/userStore";
import { Role } from "@gen/user/v1/user_pb";
import ScrollToTop from "@components/layout/ScrollToTop";
import { UserDetails } from "@pages/admin/users/UserDetails";
import UserBookings from "@pages/user/bookings/UserBookings";
import Options from "@pages/booking/options/Options";
import { ExchangeSearch } from "@pages/booking/exchange/Search";
import { ExchangeRecap } from "@pages/booking/exchange/Recap";
import { Exchange } from "@pages/booking/exchange/Exchange";
import { ExchangeResults } from "@pages/booking/exchange/Results";
import { Contact } from "@/pages/contact/Contact";
import { Placement } from "@pages/booking/placement/Placement";
import { Passports } from "@pages/booking/passport/Passport";
import { CookiePrompt } from "@components/common/CookiePrompt";
import { PrivacyPolicy } from "@pages/legal/PrivacyPolicy";
import { TermsAndConditions } from "@pages/legal/TermsAndConditions";
import { LegalNotice } from "@pages/legal/LegalNotice";
import * as Sentry from "@sentry/react";

const SentryRoutes = Sentry.wrapReactRouterRouting(Routes);

function App() {
  const fetchUser = useUserStore((s) => s.fetchUser);

  useEffect(() => {
    fetchUser();
    const onVisible = () => {
      if (document.visibilityState === "visible") void fetchUser();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchUser]);

  return (
    <ThemeProvider theme={themeCnam}>
      <ToasterProvider>
        <BrowserRouter>
          <ScrollToTop>
            <div className="flex flex-col h-screen w-full">
              <Header />
              {/* Margin top values for fixed header height on mobile and desktop */}
              <div className="flex flex-1 mt-[113px] lg:mt-[161px]">
                <SentryRoutes>
                  <Route
                    path={routes.BOOKING}
                    element={
                      <ProtectedRoute>
                        <BookingHome />
                      </ProtectedRoute>
                    }
                  >
                    <Route index path={routes.SEARCH} element={<Search />} />
                    <Route path={routes.RESULTS} element={<Results />} />
                    <Route path={routes.PLACEMENTS} element={<Placement />} />
                    <Route path={routes.OPTIONS} element={<Options />} />
                    <Route path={routes.PASSPORT} element={<Passports />} />
                    <Route path={routes.RECAP} element={<Recap />} />
                    <Route path={routes.CART} element={<Cart />} />
                    <Route path={routes.PAYMENT} element={<Payment />} />
                    <Route
                      path={routes.CONFIRMATION}
                      element={<Confirmation />}
                    />
                  </Route>

                  <Route
                    path={routes.EXCHANGE}
                    element={
                      <ProtectedRoute>
                        <Exchange />
                      </ProtectedRoute>
                    }
                  >
                    <Route
                      index
                      path={routes.EXCHANGE_SEARCH}
                      element={<ExchangeSearch />}
                    />
                    <Route
                      path={routes.EXCHANGE_RESULTS}
                      element={<ExchangeResults />}
                    />
                    <Route
                      path={routes.EXCHANGE_RECAP}
                      element={<ExchangeRecap />}
                    />
                  </Route>

                  <Route
                    path={routes.REGISTRATION}
                    element={<Registration />}
                  />
                  <Route path={routes.LOGIN} element={<Login />} />
                  <Route
                    path={routes.VERIFICATION}
                    element={<Verification />}
                  />
                  <Route path={routes.RECOVERY} element={<Recovery />} />
                  <Route path={routes.SETTINGS} element={<Settings />} />
                  <Route path={routes.ERROR} element={<Error />} />
                  <Route path={routes.HOME} element={<Home />} />
                  <Route
                    path={routes.USER_BOOKINGS}
                    element={<UserBookings />}
                  />
                  <Route path={routes.HELP} element={<Help />} />
                  <Route
                    path={routes.ADMIN}
                    element={
                      <ProtectedRoute>
                        <AdminHome />
                      </ProtectedRoute>
                    }
                  >
                    <Route
                      path={routes.AGENCY_MANAGEMENT}
                      element={
                        <ProtectedRoute roles={[Role.ADMIN, Role.TRAVEL_AGENT]}>
                          <AgencyManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={routes.CAISSE_MANAGEMENT}
                      element={
                        <ProtectedRoute
                          roles={[
                            Role.ADMIN,
                            Role.TRAVEL_AGENT,
                            Role.CAISSE_ASSOCIATE,
                          ]}
                        >
                          <CaisseManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={routes.CAISSE_DETAILS}
                      element={
                        <ProtectedRoute
                          roles={[
                            Role.ADMIN,
                            Role.TRAVEL_AGENT,
                            Role.CAISSE_ASSOCIATE,
                          ]}
                        >
                          <CaisseDetails />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={routes.DAP_MANAGEMENT}
                      element={
                        <ProtectedRoute
                          roles={[
                            Role.ADMIN,
                            Role.TRAVEL_AGENT,
                            Role.CAISSE_ASSOCIATE,
                          ]}
                        >
                          <DAPManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={routes.DAP_DETAILS}
                      element={
                        <ProtectedRoute
                          roles={[
                            Role.ADMIN,
                            Role.TRAVEL_AGENT,
                            Role.CAISSE_ASSOCIATE,
                          ]}
                        >
                          <DAPDetails />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={routes.USER_MANAGEMENT}
                      element={
                        <ProtectedRoute roles={[Role.ADMIN, Role.TRAVEL_AGENT]}>
                          <UserManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={routes.USER_DETAILS}
                      element={
                        <ProtectedRoute roles={[Role.ADMIN, Role.TRAVEL_AGENT]}>
                          <UserDetails />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="" element={<Navigate to="/" replace />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Route>

                  <Route
                    path={routes.PRIVACY_POLICY}
                    element={<PrivacyPolicy />}
                  />
                  <Route
                    path={routes.TERMS_AND_CONDITIONS}
                    element={<TermsAndConditions />}
                  />
                  <Route path={routes.LEGAL_NOTICE} element={<LegalNotice />} />

                  <Route
                    path="*"
                    element={<Navigate to={routes.HOME} replace />}
                  />
                  <Route path={routes.CONTACT} element={<Contact />} />
                </SentryRoutes>
                <CookiePrompt />
                <GlobalFreshChat />
              </div>
              <Footer />
            </div>
          </ScrollToTop>
        </BrowserRouter>
      </ToasterProvider>
    </ThemeProvider>
  );
}

export default App;
